
import { calculateDistance } from '../../math/geo';
import { TORTUOSITY_FACTOR } from '../constants';
import { getOsrmRoute } from '../../routing/osrm';
import { getEffectivedSpeedMinPerKm, COLD_START_MINS_PER_KM } from '../constants';

/**
 * v55.0: S.A.L. DISTANCE ENGINE - SINGLE SOURCE OF TRUTH
 * Centraliza toda a lógica de distância para evitar divergências entre UI e Lógica.
 */
export const DistanceService = {
    /**
     * Retorna a distância corrigida (Km). 
     * Prioriza OSRM se mode for 'precise'.
     */
    getDistance: async (
        lat1: number, lng1: number,
        lat2: number, lng2: number,
        mode: 'precise' | 'fast' = 'fast'
    ): Promise<number> => {
        if (mode === 'precise') {
            const osrm = await getOsrmRoute(lat1, lng1, lat2, lng2);
            if (osrm) {
                // v55.2: Safety Scalar (1.10)
                // OSRM público tende a ser "otimista" demais no Brasil. 
                // Aplicamos um ajuste de 10% para alinhar com a realidade prática do Google Maps.
                return osrm.distanceKm * 1.10;
            }
        }

        // Global Source of Truth: Haversine * Fator Centralizado
        return calculateDistance(lat1, lng1, lat2, lng2) * TORTUOSITY_FACTOR;
    },

    /**
     * Versão síncrona para loops intensos (Clustering/Insertion).
     * Aplica SEMPRE o TORTUOSITY_FACTOR para consistência.
     */
    getDistanceSync: (
        lat1: number, lng1: number,
        lat2: number, lng2: number
    ): number => {
        return calculateDistance(lat1, lng1, lat2, lng2) * TORTUOSITY_FACTOR;
    },

    /**
     * Tempo de deslocamento em minutos.
     * Prioridade: velocidade aprendida (km/h) > speedFactor > fallback universal.
     */
    getTravelTime: (
        distanceKm: number,
        type: 'own' | 'third',
        driver?: { speedFactor?: number; avgSpeedKmH?: number }
    ): number => {
        const safeDistance = Math.max(0, distanceKm || 0);
        if (safeDistance === 0) return 0;

        // Terceiros sem telemetria de piloto usam fallback fixo.
        if (type === 'third') {
            return Math.max(1, safeDistance * COLD_START_MINS_PER_KM);
        }

        // Frota propria com velocidade aprendida valida.
        if (driver?.avgSpeedKmH) {
            const learnedMinPerKm = getEffectivedSpeedMinPerKm(driver.avgSpeedKmH);
            return Math.max(1, safeDistance * learnedMinPerKm);
        }

        // Frota propria com speedFactor (multiplicador de tempo).
        if (driver?.speedFactor && driver.speedFactor > 0) {
            return Math.max(1, (safeDistance * COLD_START_MINS_PER_KM) / driver.speedFactor);
        }

        return Math.max(1, safeDistance * COLD_START_MINS_PER_KM);
    }
};
